export class SignalBinding {
    constructor(signal: Signal, listener, isOnce: boolean, listenerContext, priority?: number) {
        this._listener = listener;
        this._isOnce = isOnce;
        this.context = listenerContext;
        this._signal = signal;
        this.priority = priority || 0;
    }

    private _listener;
    private _isOnce: boolean;
    public context;
    private _signal: Signal | null;
    public priority: number;
    public active: boolean = true;
    public params: null | any[] = null;

    public execute(paramsArr?: any[]) {
        let handlerReturn;
        let params;

        if (this.active && !!this._listener) {
            params = this.params ? this.params.concat(paramsArr) : paramsArr;

            handlerReturn = this._listener.apply(this.context, params);

            if (this._isOnce) {
                this.detach();
            }
        }

        return handlerReturn;
    }

    public detach() {
        return this.isBound() ? this._signal!.remove(this._listener, this.context) : null;
    }

    public isBound(): boolean {
        return !!this._signal && !!this._listener;
    }

    public isOnce(): boolean {
        return this._isOnce;
    }

    public getListener() {
        return this._listener;
    }

    public getSignal() {
        return this._signal;
    }

    public _destroy() {
        this._signal = null;
        delete this._listener;
        delete this.context;
    }

    public toString(): string {
        return (
            "[SignalBinding isOnce:" + this._isOnce + ", isBound:" + this.isBound() + ", active:" + this.active + "]"
        );
    }
}

export class Signal {
    private _bindings: SignalBinding[] = [];
    private _prevParams: null | any[] = null;
    public memorize: boolean = false;
    private _shouldPropagate: boolean = true;
    public active: boolean = true;

    public validateListener(listener, fnName) {
        if (typeof listener !== "function")
            throw new Error("listener is a required param of {fn}() and should be a Function.".replace("{fn}", fnName));
    }

    private _registerListener(listener, isOnce: boolean, listenerContext, priority: number): SignalBinding {
        const prevIndex: number = this._indexOfListener(listener, listenerContext);
        let binding: SignalBinding;

        if (prevIndex !== -1) {
            binding = this._bindings[prevIndex];

            if (binding.isOnce() !== isOnce) {
                throw new Error(
                    "You cannot add" +
                        (isOnce ? "" : "Once") +
                        "() then add" +
                        (!isOnce ? "" : "Once") +
                        "() the same listener without removing the relationship first."
                );
            }
        } else {
            binding = new SignalBinding(this, listener, isOnce, listenerContext, priority);

            this._addBinding(binding);
        }

        if (this.memorize && this._prevParams) {
            binding.execute(this._prevParams);
        }

        return binding;
    }

    private _addBinding(binding: SignalBinding) {
        //simplified insertion sort

        let n: number = this._bindings.length;

        do {
            --n;
        } while (this._bindings[n] && binding.priority <= this._bindings[n].priority);

        this._bindings.splice(n + 1, 0, binding);
    }

    private _indexOfListener(listener, context): number {
        let n: number = this._bindings.length;
        let cur: SignalBinding;

        while (n--) {
            cur = this._bindings[n];

            if (cur.getListener() === listener && cur.context === context) {
                return n;
            }
        }

        return -1;
    }

    public has(listener, context: any = null): boolean {
        return this._indexOfListener(listener, context) !== -1;
    }

    public add(listener, listenerContext: any = null, priority: number = 0): SignalBinding {
        this.validateListener(listener, "add");
        return this._registerListener(listener, false, listenerContext, priority);
    }

    public addOnce(listener, listenerContext: any = null, priority: number = 0): SignalBinding {
        this.validateListener(listener, "addOnce");
        return this._registerListener(listener, true, listenerContext, priority);
    }

    public remove(listener, context: any = null) {
        this.validateListener(listener, "remove");
        const i: number = this._indexOfListener(listener, context);

        if (i !== -1) {
            this._bindings[i]._destroy(); //no reason to a SignalBinding exist if it isn't attached to a signal
            this._bindings.splice(i, 1);
        }

        return listener;
    }

    public removeAll() {
        let n: number = this._bindings.length;

        while (n--) {
            this._bindings[n]._destroy();
        }

        this._bindings.length = 0;
    }

    public getNumListeners(): number {
        return this._bindings.length;
    }

    public halt() {
        this._shouldPropagate = false;
    }

    public dispatch(...paramsArr: any[]) {
        if (!this.active) return;

        let n: number = this._bindings.length;

        if (this.memorize) {
            this._prevParams = paramsArr;
        }

        if (!n) {
            //should come after memorize
            return;
        }

        const bindings = this._bindings.slice(0); //clone array in case add/remove items during dispatch
        this._shouldPropagate = true; //in case `halt` was called before dispatch or during the previous dispatch.
        do {
            n--;
        } while (bindings[n] && this._shouldPropagate && bindings[n].execute(paramsArr) !== false);
    }

    public forget() {
        this._prevParams = null;
    }

    public dispose() {
        this.removeAll();
        this._bindings = [];
        this._prevParams = null;
    }

    public toString(): string {
        return "[Signal active:" + this.active + " numListeners:" + this.getNumListeners() + "]";
    }
}

export class TypedSignal<T> extends Signal {
    public add(listener: (param: T) => void, listenerContext: any = null, priority: number = 0): SignalBinding {
        return super.add(listener, listenerContext, priority);
    }

    public addOnce(listener: (param: T) => void, listenerContext: any = null, priority: number = 0): SignalBinding {
        return super.addOnce(listener, listenerContext, priority);
    }

    public dispatch(param: T) {
        super.dispatch(param);
    }

    public remove(listener: (param: T) => void, context: any = null) {
        return super.remove(listener, context);
    }

    public has(listener: (param: T) => void, context: any = null): boolean {
        return super.has(listener, context);
    }
}
